import { create } from 'zustand';
import { User, UserRole } from '@/types';
import { api } from '@/lib/api/client';
import { AppError } from '@/utils/errors/AppError';

interface AuthState {
  user: User | null;
  users: User[];
  isLoading: boolean;
  error: string | null;
  
  initializeAuth: () => Promise<void>;
  login: (phoneNumber: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  
  // User management
  fetchUsers: () => Promise<void>;
  addUser: (userData: { username: string; phoneNumber: string; password: string; role: UserRole }) => Promise<void>;
  updateUsername: (userId: string, username: string) => Promise<void>;
  updateUserPhone: (userId: string, phoneNumber: string) => Promise<void>;
  updateUserPassword: (userId: string, password: string) => Promise<void>;
  deleteUser: (userId: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  users: [],
  isLoading: false,
  error: null,

  initializeAuth: async () => {
    const sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      set({ user: null, isLoading: false, error: null });
      return;
    }

    try {
      set({ isLoading: true, error: null });
      const { user } = await api.get('api/auth/verify');
      set({ user, isLoading: false, error: null });
    } catch (error) {
      console.warn('Auth verification failed:', error);
      localStorage.removeItem('sessionId');
      set({ user: null, isLoading: false, error: null });
    }
  },

  login: async (phoneNumber: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.post<{ user: User; sessionId: string }>(
        'api/auth/login',
        { phoneNumber, password }
      );

      localStorage.setItem('sessionId', data.sessionId);
      set({ user: data.user, isLoading: false, error: null });
      return true;
    } catch (error) {
      let message = 'فشل تسجيل الدخول';
      
      if (error instanceof AppError) {
        message = error.message;
      }

      set({ isLoading: false, error: message });
      return false;
    }
  },

  logout: async () => {
    try {
      await api.post('api/auth/logout');
    } catch (error) {
      console.warn('Logout error:', error);
    } finally {
      localStorage.removeItem('sessionId');
      set({ user: null, isLoading: false, error: null });
    }
  },

  fetchUsers: async () => {
    const state = get();
    if (state.isLoading) return;

    // Only show loading if we don't have data
    const shouldShowLoading = !state.users.length;
    if (shouldShowLoading) {
      set({ isLoading: true, error: null });
    }

    try {
      const users = await api.get<User[]>('api/users');
      set({ users, isLoading: false, error: null });
    } catch (error) {
      console.error('Failed to fetch users:', error);
      set({ 
        isLoading: false, 
        error: error instanceof AppError ? error.message : 'Failed to fetch users'
      });
    }
  },

  addUser: async (userData) => {
    set({ isLoading: true, error: null });
    try {
      const newUser = await api.post<User>('api/users', userData);
      set(state => ({
        users: [...state.users, newUser],
        isLoading: false,
        error: null
      }));
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to add user'
      });
      throw error;
    }
  },

  updateUserPhone: async (userId: string, phoneNumber: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`api/users/${userId}/update-phone`, { phoneNumber });
      
      set(state => ({
        users: state.users.map(user =>
          user.id === userId ? { ...user, phoneNumber } : user
        ),
        isLoading: false,
        error: null
      }));
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to update phone number'
      });
      throw error;
    }
  },

  updateUsername: async (userId: string, username: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`api/users/${userId}/update-username`, { username });
      
      set(state => ({
        users: state.users.map(user =>
          user.id === userId ? { ...user, username } : user
        ),
        isLoading: false,
        error: null
      }));
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to update Username'
      });
      throw error;
    }
  },

  updateUserPassword: async (userId: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`api/users/${userId}/update-password`, { password });
      set({ isLoading: false, error: null });
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to update password'
      });
      throw error;
    }
  },

  deleteUser: async (userId: string) => {
    set({ isLoading: true, error: null });
    try {
      await api.post(`api/users/${userId}/delete`);
      
      set(state => ({
        users: state.users.filter(user => user.id !== userId),
        isLoading: false,
        error: null
      }));
    } catch (error) {
      set({ 
        isLoading: false,
        error: error instanceof AppError ? error.message : 'Failed to delete user'
      });
      throw error;
    }
  }
}));